import React from "react"
import {
    Box,
    Button,
    ColorSchemes,
    componentStyles,
    HeadingMedium,
    Text,
    useColors,
    VerticalStack,
} from "@mallardbay/lib-react-components"

import Image from "~components/shared/image"

export default function ActionMessageContent({
    title,
    message,
    image,
    titleTestId,
    messageTestId,
    buttonLabel,
    buttonTestId,
    onButtonClick,
}: Props) {
    const styles = useStyles({ hasImage: Boolean(image) })
    const shouldRenderButton = Boolean(buttonLabel && onButtonClick)

    return (
        <VerticalStack style={styles.container}>
            {image ? (
                <Box style={styles.imageWrapper}>
                    <Image
                        src={image.src}
                        alt={image.alt}
                        testId={image.testId}
                        style={styles.image}
                    />
                </Box>
            ) : null}

            <VerticalStack style={styles.textContent}>
                <HeadingMedium testId={titleTestId}>{title}</HeadingMedium>

                <Text style={styles.message} testId={messageTestId}>
                    {message}
                </Text>
            </VerticalStack>

            {shouldRenderButton ? (
                <Button
                    colorScheme={ColorSchemes.Brand}
                    onClick={onButtonClick}
                    testId={buttonTestId}
                >
                    {buttonLabel}
                </Button>
            ) : null}
        </VerticalStack>
    )
}

function useStyles({ hasImage }: StyleOptions) {
    const colors = useColors()

    return componentStyles({
        container: {
            alignItems: "center",
            textAlign: "center",
            gap: hasImage ? 6 : 16,
            paddingTop: 16,
            paddingBottom: hasImage ? 32 : 16,
        },
        imageWrapper: {
            width: "100%",
            height: 220,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
        },
        image: {
            width: "100%",
            maxWidth: {
                base: 300,
                md: 400,
            },
            objectFit: "contain",
            borderRadius: 12,
        },
        textContent: {
            gap: 8,
            alignItems: "center",
        },
        message: {
            color: colors.secondaryText,
            maxWidth: 420,
            lineHeight: 1.6,
        },
    })
}

type Props = {
    readonly title: string
    readonly message: string
    readonly image?: ActionMessageImage
    readonly titleTestId?: string
    readonly messageTestId?: string
    readonly buttonLabel?: string
    readonly buttonTestId?: string
    readonly onButtonClick?: () => void
}

type ActionMessageImage = {
    readonly src: string
    readonly alt: string
    readonly testId?: string
}

type StyleOptions = {
    readonly hasImage: boolean
}
